import React, {
  useEffect,
  useMemo,
  useRef,
  useLayoutEffect,
  useState,
} from "react";
import * as d3 from "d3";
import "./DataEstateSensitiveCard.scss";
import { useMediaQuery } from "material-ui-core";

const CLASSIFICATION_COLORS = {
  PCI: "#e74c3c",
  PII: "#3498db",
  "PHI/HIPAA": "#16a085",
  GDPR: "#9b59b6",
  Confidential: "#27ae60",
  Financial: "#f1c40f",
};

const DOT_RADIUS = 2;
const COLLAPSED_DOT_CAP = 300;

const seededRandom = (seed) => {
  let x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
};

const distance = (x1, y1, x2, y2) =>
  Math.sqrt((x1 - x2) ** 2 + (y1 - y2) ** 2);

export default function RegulatedScatterChart({
  data,
  expanded = false,
  activeFilter = null,
}) {
  const svgRef = useRef(null);
  const [size, setSize] = useState([0, 0]);

  const isSmall = useMediaQuery("(max-width:1600px)");
  const isMedium = useMediaQuery("(max-width:1800px)");

  useLayoutEffect(() => {
    const resize = () => {
      if (!svgRef.current) return;
      setSize([
        svgRef.current.offsetWidth,
        svgRef.current.offsetHeight,
      ]);
    };

    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const normalizedData = useMemo(() => {
    if (!data) return [];
    if (Array.isArray(data)) return data;

    return Object.entries(data).map(([combo, count]) => ({
      "FileClassificationStats.classificationCombination": combo,
      "FileClassificationStats.fileCount": count,
    }));
  }, [data]);

  const circleSizes = useMemo(() => {
    const map = new Map();

    normalizedData.forEach((item) => {
      const sets =
        item["FileClassificationStats.classificationCombination"]
          .split(",")
          .map((s) => s.trim());

      const count = Number(
        item["FileClassificationStats.fileCount"]
      );

      sets.forEach((s) => {
        map.set(s, (map.get(s) || 0) + count);
      });
    });

    return map;
  }, [normalizedData]);


  const layoutConfig = useMemo(() => {
    if (expanded) {
      return {
        minRadius: 35,
        maxRadius: 95,
        innerRing: 90,
        outerRing: 160,
      };
    }

    if (isSmall) {
      return {
        minRadius: 5,
        maxRadius: 20,
        innerRing: 15,
        outerRing: 40,
      };
    }

    if (isMedium) {
      return {
        minRadius: 10,
        maxRadius: 25,
        innerRing: 20,
        outerRing: 50,
      };
    }

    return {
      minRadius: 15,
      maxRadius: 30,
      innerRing: 30,
      outerRing: 70,
    };
  }, [expanded, isSmall, isMedium]);

  useEffect(() => {
    if (!svgRef.current || !circleSizes.size) return;
    if (!size[0] || !size[1]) return;

    d3.select(svgRef.current).selectAll("*").remove();

    const width = size[0];
    const height = size[1];
    const cx = width / 2;
    const cy = height / 2;

    const svg = d3
      .select(svgRef.current)
      .append("svg")
      .attr("width", width)
      .attr("height", height)
      .style("pointer-events", "all");

    const tooltip = d3
      .select("body")
      .append("div")
      .attr("class", "venn-tooltip")
      .style("position", "absolute")
      .style("z-index", 9999)
      .style("background", "#fff")
      .style("padding", "8px")
      .style("border-radius", "4px")
      .style("font-size", "12px")
      .style("pointer-events", "none")
      .style("opacity", 0);

    const nodes = Array.from(circleSizes.entries())
      .map(([type, value]) => ({ type, value }))
      .sort((a, b) => b.value - a.value);

    const radiusScale = d3
      .scaleSqrt()
      .domain([0, d3.max(nodes, (d) => d.value)])
      .range([layoutConfig.minRadius, layoutConfig.maxRadius]);

    nodes.forEach((d) => (d.r = radiusScale(d.value)));

    const split = Math.ceil(nodes.length / 2);
    const inner = nodes.slice(0, split);
    const outer = nodes.slice(split);

    inner.forEach((d, i) => {
      const a = (2 * Math.PI * i) / inner.length;
      d.x = cx + Math.cos(a) * layoutConfig.innerRing;
      d.y = cy + Math.sin(a) * layoutConfig.innerRing;
    });

    outer.forEach((d, i) => {
      const a = (2 * Math.PI * i) / outer.length;
      d.x = cx + Math.cos(a) * layoutConfig.outerRing;
      d.y = cy + Math.sin(a) * layoutConfig.outerRing;
    });

    const dotsLayer = svg.append("g");

    nodes.forEach((d, nodeIndex) => {
      const dotCount = expanded
        ? Math.round(d.value)
        : Math.min(Math.round(d.value), COLLAPSED_DOT_CAP);

      const color =
        !activeFilter || d.type === activeFilter
          ? CLASSIFICATION_COLORS[d.type]
          : "#9ca3af";

      const opacity =
        !activeFilter || d.type === activeFilter ? 0.9 : 0.25;

      for (let i = 0; i < dotCount; i++) {
        const r = d.r * Math.sqrt(seededRandom(nodeIndex * 100000 + i * 13));
        const angle =
          seededRandom(nodeIndex * 200000 + i * 29) * Math.PI * 2;

        let x = d.x + r * Math.cos(angle);
        let y = d.y + r * Math.sin(angle);

        nodes.forEach((other) => {
          if (other.type === d.type) return;
          if (distance(x, y, other.x, other.y) < other.r) {
            const dx = x - other.x;
            const dy = y - other.y;
            const len = Math.sqrt(dx * dx + dy * dy) || 1;
            x += (dx / len) * 2;
            y += (dy / len) * 2;
          }
        });

        dotsLayer
          .append("circle")
          .attr("cx", x)
          .attr("cy", y)
          .attr("r", DOT_RADIUS)
          .attr("fill", color)
          .attr("opacity", opacity)
          .on("mouseover", () => {
            tooltip
              .style("opacity", 1)
              .html(`<strong>${d.type}</strong><br/>Files: ${d.value}`);
          })
          .on("mousemove", (e) =>
            tooltip
              .style("left", e.pageX + 10 + "px")
              .style("top", e.pageY - 28 + "px")
          )
          .on("mouseout", () => tooltip.style("opacity", 0));
      }
    });

    const labels = svg
      .selectAll("g.label")
      .data(nodes)
      .enter()
      .append("g");

    labels
      .append("text")
      .attr("x", (d) => d.x)
      .attr("y", (d) => d.y)
      .attr("text-anchor", "middle")
      .attr("dy", "0.35em")
      .text((d) => d.type)
      .style("fill", (d) =>
        activeFilter && d.type !== activeFilter
          ? "#9ca3af"
          : d3.color(CLASSIFICATION_COLORS[d.type]).darker(1.2)
      )
      .style("font-weight", "800")
      .style("font-size", expanded ? "16px" : "12px")
      .style("pointer-events", "none");

    return () => tooltip.remove();
  }, [
    circleSizes,
    activeFilter,
    size,
    expanded,
    layoutConfig,
  ]);

  return <div className="ss-canvas" ref={svgRef} />;
}
